import React, { useEffect, useRef } from "react";
import WaveSurfer from "wavesurfer.js";
import { getThemeMode, local } from "../utils/helper";
import { useSelector } from "react-redux";
import ConsoleUtils from "../utils/console";

const globalWaveSurfers = [];

export default function Waveform({ url, isPlay, setPlay, message }) {
  const { widgetConfig } = useSelector((state) => ({
    widgetConfig: state.messages.widgetConfig,
  }));
  const waveformRef = useRef(null);
  const wavesurfer = useRef(null);

  const formWaveSurferOptions = (ref, isBgLight) => ({
    container: ref,
    waveColor: !isBgLight && message?.from === "customer" || message?.data?.output ? "#383737" : "#C8C8C8",
    progressColor: !isBgLight && message?.from === "customer" || message?.data?.output ? "#C8C8C8" : "#383737",
    cursorColor: !isBgLight && message?.from === "customer" || message?.data?.output ? "#C8C8C8" : "#383737",
    barWidth: 2,
    barRadius: 5,
    responsive: true,
    height: 20,
    normalize: true,
    partialRender: true
  });

  // Function to initialize WaveSurfer
  const initializeWaveSurfer = () => {
    if (waveformRef?.current) {
      const isBgLight = getThemeMode(widgetConfig?.widgetConfig?.lookAndFeel?.backgroundColor);
      const options = formWaveSurferOptions(waveformRef?.current, isBgLight);

      if (wavesurfer?.current) {
        wavesurfer?.current.destroy();
        wavesurfer.current = null;
      }

      wavesurfer.current = WaveSurfer?.create(options);
      wavesurfer?.current?.load(url);

      globalWaveSurfers.push(wavesurfer.current);

      wavesurfer?.current?.on("play", () => {
        // Pause all other WaveSurfer instances
        globalWaveSurfers.forEach((ws) => {
          if (ws !== wavesurfer.current && ws.isPlaying()) {
            setPlay(false);
            ws.pause();
          }
        });
      });

      wavesurfer?.current?.on("finish", () => {
        setPlay(false); // Update the play state to `false`
        wavesurfer?.current?.seekTo(0); // Reset the audio to the start
      });
    }
  };

  // Initial setup and load of WaveSurfer
  useEffect(() => {
    setPlay(false);
    initializeWaveSurfer();

    return () => {
      if (wavesurfer?.current && wavesurfer?.current?.isReady) {
        const index = globalWaveSurfers.indexOf(wavesurfer.current);
        if (index > -1) globalWaveSurfers.splice(index, 1);

        wavesurfer?.current.destroy();
        wavesurfer.current = null;
      } else {
        ConsoleUtils.warn("WaveSurfer not ready, delaying destruction");
      }
    };
  }, [url]);

  // Re-initialize WaveSurfer when `message` changes (for dynamic colors)
  useEffect(() => {
    try {
      if (wavesurfer?.current && wavesurfer?.current?.isReady) {
        wavesurfer?.current.destroy();
        initializeWaveSurfer();
      }
    } catch (error) {
      ConsoleUtils.error("Error during WaveSurfer reinitialization:", error);
    }
  }, [message]);

  // Play or pause the audio based on `isPlay`
  useEffect(() => {
    if (wavesurfer?.current) {
      if (isPlay) {
        wavesurfer?.current?.play();
      } else {
        wavesurfer?.current?.pause();
      }
    }
  }, [isPlay]);

  return <div id="waveform" ref={waveformRef} />;
}
